import { Injectable } from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {map, mergeMap} from 'rxjs/operators';
import {ResultListApiModel} from '../../api/models/result-list.api.model';
import {ProjectApiModel} from '../../api/models/project.api-model';
import {VehicleViewModel} from '../user/garage/vehicle.view-model';
import {MediaObjectViewModel} from '../../core/models/media-object.view-model';
import {ProjectViewModel} from '../user/garage/project.view-model';
import {QueryParamsApiModel} from '../../api/models/query-params-api.model';
import {FilterParameterApiModel} from '../../api/models/filter-parameter.api.model';
import {MediaObjectApiModel} from '../../api/models/media-object.api-model';
import {VehicleTypeViewModel} from '../../core/models/vehicle-type.view-model';
import {VehicleTypeApiModel} from '../../api/models/vehicle-type.api-model';
import {ProjectRestService} from '../../api/services/project.rest.service';
import {VehicleTypeRestService} from '../../api/services/vehicle-type.rest.service';
import {MediaObjectRestService} from '../../api/services/media-object.rest.service';
import {UserApiModel} from '../../api/models/user.api-model';
import {UserViewModel} from '../user/settings/user.view-model';
import {RankingCategory} from './ranking-category.type';
import {UserRestService} from '../../api/services/user.rest.service';
import {RankingUserApiModel} from './ranking-user.api-model';
import {RankingUserViewModel} from './ranking-user.view-model';
import {VehicleReadHydrator} from '../user/garage/vehicle-read-hydrator.service';

export interface RankingCollectionViewModel<T> {
  records: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root'
})
export class RankingDataProviderService {

  constructor(
    private readonly projectRS: ProjectRestService,
    private readonly userRS: UserRestService,
    private readonly vehicleHydrator: VehicleReadHydrator,
    private readonly vehicleTypeRS: VehicleTypeRestService,
    private readonly mediaObjectRS: MediaObjectRestService,
  ) { }

  fetchRankingResources(): Observable<ProjectViewModel[]> {
    const params = new QueryParamsApiModel();
    params.pagination = false;

    return this.fetchProjectRanking('overall', params).pipe(map((result) => result.records));
  }

  fetchProjectRanking(category: Exclude<RankingCategory, 'users'>, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<RankingCollectionViewModel<ProjectViewModel>> {
    return this.projectRS.getRatedCollection(queryParamsApiModel, category).pipe(mergeMap((resultListAM: ResultListApiModel<ProjectApiModel>) => {
      const deps = forkJoin([
        this.getVehicleCollection(resultListAM.records),
        this.getMediaObjectCollection(resultListAM.records)
      ]);

      return deps.pipe(map(([vehicleVMs, mediaObjectVMs]: [VehicleViewModel[], MediaObjectViewModel[]]) => ({
        records: resultListAM.records.map((projectAM: ProjectApiModel) => {
          const projectVM = new ProjectViewModel(projectAM);
          projectVM.vehicle = vehicleVMs.find(vehicle => vehicle['@id'] === projectVM.vehicle?.['@id']) || projectVM.vehicle;

          if (projectVM.vehicle?.vehicleType?.code) {
            projectVM.vehicleType = projectVM.vehicle.vehicleType.code as any;
          }

          const user = projectAM.user as unknown as UserApiModel;
          projectVM.user = new UserViewModel(user);

          if (typeof projectAM.theme === 'string') {
            projectVM.theme = mediaObjectVMs.find((media) => media['@id'] === projectAM.theme) || null;
          }

          return projectVM;
        }),
        total: resultListAM.total,
        page: resultListAM.page,
        limit: resultListAM.limit,
        totalPages: resultListAM.totalPages,
      })));
    }));
  }

  fetchUserRanking(queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<RankingCollectionViewModel<RankingUserViewModel>> {
    return this.userRS.getRankingCollection(queryParamsApiModel).pipe(
      mergeMap((resultListAM: ResultListApiModel<RankingUserApiModel>) => this.hydrateRankingUsers(resultListAM.records).pipe(
        map((records) => ({
          records,
          total: resultListAM.total,
          page: resultListAM.page,
          limit: resultListAM.limit,
          totalPages: resultListAM.totalPages,
        }))
      ))
    );
  }

  public getMediaObjectCollection(projectAMs: ProjectApiModel[]): Observable<MediaObjectViewModel[]> {
    const params = new QueryParamsApiModel();
    params.filters = [...new Set(projectAMs
      .filter((project: ProjectApiModel) => typeof project.theme === 'string')
      .map(project => project.theme as string))].map(iri => new FilterParameterApiModel('id', iri));
    if (params.filters.length) {
      return this.mediaObjectRS.getMediaObjectCollection(params)
        .pipe(map((resultListAM: ResultListApiModel<MediaObjectApiModel>) => {
          return resultListAM.records.map((mediaObjectAM: MediaObjectApiModel) => new MediaObjectViewModel(mediaObjectAM));
        }));
    }

    return of<MediaObjectViewModel[]>([]);
  }

  public getVehicleCollection(projectAMs: ProjectApiModel[]): Observable<VehicleViewModel[]> {
    return this.vehicleHydrator.hydrate(projectAMs.map(project => project.vehicle));
  }

  public getVehicleTypeCollection(queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<VehicleTypeViewModel[]> {
    return this.vehicleTypeRS.getVehicleTypeCollection(queryParamsApiModel)
      .pipe(map((resultListAM: ResultListApiModel<VehicleTypeApiModel>) => {
        return resultListAM.records.map((vehicleAM: VehicleTypeApiModel) => new VehicleTypeViewModel(vehicleAM));
      }));
  }

  private hydrateRankingUsers(records: RankingUserApiModel[]): Observable<RankingUserViewModel[]> {
    const params = new QueryParamsApiModel();
    params.pagination = false;
    params.filters = records
      .map((record) => record.user?.avatar)
      .filter((avatar): avatar is string => typeof avatar === 'string')
      .map((avatar) => new FilterParameterApiModel('id', avatar));

    if (!params.filters.length) {
      return of(records.map((record) => new RankingUserViewModel(record)));
    }

    return this.mediaObjectRS.getMediaObjectCollection(params).pipe(
      map((resultListAM: ResultListApiModel<MediaObjectApiModel>) => resultListAM.records),
      map((mediaObjects) => records.map((record) => {
        const viewModel = new RankingUserViewModel(record);

        if (typeof record.user?.avatar === 'string') {
          const avatar = mediaObjects.find((media) => media['@id'] === record.user.avatar);
          viewModel.user.avatar = avatar ? new MediaObjectViewModel(avatar) : null;
        }

        return viewModel;
      }))
    );
  }
}
